import { useState, useEffect, type RefObject } from 'react';
import {
  getCanScrollLeft,
  getCanScrollRight,
} from '../utils/get-tab-scroll-px';

/**
 * Simple hook to handle tab scroll state
 * Only tracks scroll position for arrow visibility
 */
export function useTabScrollObserver(tabListRef: RefObject<HTMLDivElement>) {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const container = tabListRef.current;
    if (!container) return;

    const checkScrollState = () => {
      setCanScrollLeft(getCanScrollLeft({ ref: tabListRef }));
      setCanScrollRight(getCanScrollRight({ ref: tabListRef }));
    };

    // Initial check after layout
    checkScrollState();

    // Scroll listener
    container.addEventListener('scroll', checkScrollState);

    // Resize observer to react to size/content changes
    const resizeObserver = new ResizeObserver(() => checkScrollState());
    resizeObserver.observe(container);

    // Window resize as a fallback
    window.addEventListener('resize', checkScrollState);

    return () => {
      container.removeEventListener('scroll', checkScrollState);
      window.removeEventListener('resize', checkScrollState);
      resizeObserver.disconnect();
    };
  }, [tabListRef]);

  return {
    canScrollLeft,
    canScrollRight,
  };
}
